import { getEmissionFactorNIR } from "../emissions/getEmissionFactors";

import iceVehicleOptionsTemplate from "features/Model/Mechanicals/Templates/transportation/iceVehicleOptions.json";
import electricVehicleOptionsTemplate from "features/Model/Mechanicals/Templates/transportation/electricVehicleOptions.json";
import hybridVehicleOptionsTemplate from "features/Model/Mechanicals/Templates/transportation/hybridVehicleOptions.json";

import iceVehicleTemplate from "features/Model/Mechanicals/Templates/transportation/iceVehicle.json";
import hybridVehicleTemplate from "features/Model/Mechanicals/Templates/transportation/hybridVehicle.json";
import electricVehicleTemplate from "features/Model/Mechanicals/Templates/transportation/electricVehicle.json";

import chargeProgramTemplate from "features/Model/Mechanicals/Templates/transportation/chargePrograms.json";
import chargePreferenceTemplate from "features/Model/Mechanicals/Templates/transportation/chargePreferences.json";
import presetTOUTemplate from "features/Model/Mechanicals/Templates/transportation/presetTOU.json";

export const getIceVehicleOptionsTemplate = () => iceVehicleOptionsTemplate;
export const getHybridVehicleOptionsTemplate = () => hybridVehicleOptionsTemplate;
export const getElectricVehicleOptionsTemplate = () => electricVehicleOptionsTemplate;

export const getIceVehicleTemplate = () => iceVehicleTemplate;
export const getHybridVehicleTemplate = () => hybridVehicleTemplate;
export const getElectricVehicleTemplate = () => electricVehicleTemplate;

export const getChargeProgramTemplate = () => chargeProgramTemplate;
export const getChargePreferenceTemplate = () => chargePreferenceTemplate;
export const getPresetTOUTemplate = () => presetTOUTemplate;

function getFuelType(fuelType) {
    const fuelTypeMap = {
        "X": "Regular Gasoline",
        "Z": "Premium Gasoline",
        "D": "Diesel",
        "E": "E85",
        "B": "Electricity",
        "N": "Natural Gas",
        "B/Z": "Electricity / Premium Gasoline",
        "B/X": "Electricity / Regular Gasoline",
        "B/Z*": "Electricity / Premium Gasoline",
        "B/X*": "Electricity / Regular Gasoline",
    };

    return fuelTypeMap[fuelType] ?? fuelType; // fallback
}

function getVehicleClassInfo(vehicleClass) {
    const vehicleClassMap = {
        "Two-seater": "Interior Volume: N/A",
        "Minicompact": "Interior Volume: less than 2,405 L (85 cu. ft.)",
        "Subcompact": "Interior Volume: 2,405–2,830 L (85–99 cu. ft.)",
        "Compact": "Interior Volume: 2,830–3,115 L (100–109 cu. ft.)",
        "Mid-size": "Interior Volume: 3,115–3,400 L (110–119 cu. ft.)",
        "Full-size": "Interior Volume: 3,400 L (120 cu. ft.) or more",
        "Station wagon: Small": "Interior Volume: less than 3,680 L (130 cu. ft.)",
        "Station wagon: Mid-size": "Interior Volume: 3,680–4,530 L (130–159 cu. ft.)",
        "Pickup truck: Small": "Gross Vehicle Weight Rating: less than 2,722 kg (6,000 lb.)",
        "Pickup truck: Standard": "Gross Vehicle Weight Rating: 2,722–4,536 kg (6,000–9,999 lb.)", 
        "Sport utility vehicle: Small": "Gross Vehicle Weight Rating: less than 2,722 kg (6,000 lb.)",
        "Sport utility vehicle: Standard": "Gross Vehicle Weight Rating: 2,722–4,536 kg (6,000–9,999 lb.)",
        "Minivan": "Gross Vehicle Weight Rating: less than 3,856 kg (8,500 lb.)",
        "Van: Cargo": "Gross Vehicle Weight Rating: less than 3,856 kg (8,500 lb.)",
        "Van: Passenger": "Gross Vehicle Weight Rating: less than 4,536 kg (10,000 lb.)",
        "Special purpose vehicle": "Gross Vehicle Weight Rating: less than 3,856 kg (8,500 lb.)",
    };

    if (!vehicleClass) return "Unknown";

    return vehicleClassMap[vehicleClass] ?? "Unknown"; 
}

function getTransmissionInfo(transmission) {
    switch (true) {
        case !transmission:
            return "Unknown";
        case transmission.includes("A"):
            return "Automatic";
        case transmission.includes("AM"):
            return "Automated manual";
        case transmission.includes("AS"):
            return "Automatic with select shift";
        case transmission.includes("AV"):
            return "Continuously variable";
        case transmission.includes("M"):
            return "Manual";
        default:
            return "Unknown";
    }
}

function calculateAnnualDistanceDriven(commuteDistance) {
    let annualDistanceDriven = commuteDistance * 5 * 50; // commuteDistance x 5 days/wk x 50 weeks (excl. 2 weeks for holidays etc.)
    return annualDistanceDriven;
}

function calculateAnnualEmissions(annualDistanceDriven, emissions) {
    let annualEmissions = annualDistanceDriven * emissions * 0.001; // emissions factor comes from template in grams
    return annualEmissions;
}

function calculateAnnualIceFuelConsumption(annualDistanceDriven, combinedFuelEconomy) {
    let annualFuelConsumption = annualDistanceDriven * (combinedFuelEconomy / 100);
    return annualFuelConsumption;
}

function calculateAnnualIceFuelCost(annualFuelConsumption, fuelType, fuelPrices) {
    const { regularGasPrice, premiumGasPrice, dieselPrice } = fuelPrices;

    let averageFuelCost = 0;

    if (!fuelType) {
        //get fuel prices
        averageFuelCost = 0;
    }
    if (fuelType == "X" || "Regular Gas") {
        averageFuelCost = regularGasPrice;
    } else if (fuelType == "Z" || "Premium Gas") {
        averageFuelCost = premiumGasPrice;
    } else if (fuelType == "D" || "Diesel") {
        averageFuelCost = dieselPrice;
    } else {
        // other - Unknown
        averageFuelCost = 0;
    }

    let annualFuelCost = annualFuelConsumption * averageFuelCost;
    return annualFuelCost;
}

export const containsZero = (nestedDict) => {
    for (const outerKey in nestedDict) {
        for (const innerKey in nestedDict[outerKey]) {
            if (nestedDict[outerKey][innerKey] === 0) {
                return true; // found 0
            }
        }
    }
    return false; // no 0 found
};

export const getVehicleYearOptions = (vehicleMap) => {
    const yearOpts = Object.keys(vehicleMap)
        .filter((key) => /^\d{4}$/.test(key))
        .map((key) => ({
            //get all unique years (filtered for 4 chars) and map them to values/labels
            value: key,
            label: key,
        }));
    yearOpts.sort((a, b) => a - b); // Sort years in ascending order
    yearOpts.reverse(); // Sort years in descending order

    return yearOpts;
};

export const getVehicleManufacturerOptions = (vehicleYear, vehicleMap) => {
    const manufacturerOpts = vehicleYear
        ? Object.keys(vehicleMap[vehicleYear] || {}).map((key) => ({ // get manufacturer options based on the selected year
              value: key,
              label: key,
          }))
        : [];
    return manufacturerOpts;
};

export const getVehicleModelOptions = (vehicleYear, manufacturer, vehicleMap) => {
    const modelOpts =
        vehicleYear && manufacturer
            ? Object.keys(vehicleMap[vehicleYear][manufacturer] || {}).map((key) => ({
                  value: key,
                  label: key,
              }))
            : [];
    return modelOpts;
};

export const getIceVehicleSpecs = (commuteDistance, vehicleSpecs, fuelPrices) => {
    const {
        // vehicleYear,
        // manufacturer,
        // model,
        vehicleClass,
        // engineSize,
        // cylinders,
        transmission,
        emissions,
        // emissionRating,
        // smogRating,
        fuelType,
        combinedFuelEconomy,
    } = vehicleSpecs; //destructure specs

    const fuelTypeInfo = getFuelType(fuelType);
    const vehicleClassInfo = getVehicleClassInfo(vehicleClass);
    const transmissionInfo = getTransmissionInfo(transmission);
    const annualDistanceDriven = calculateAnnualDistanceDriven(commuteDistance);
    const annualEmissions = calculateAnnualEmissions(annualDistanceDriven, emissions);
    const annualFuelConsumption = calculateAnnualIceFuelConsumption(annualDistanceDriven, combinedFuelEconomy);
    const annualFuelCost = calculateAnnualIceFuelCost(annualFuelConsumption, fuelType, fuelPrices);

    return {
        //return values
        fuelTypeInfo,
        vehicleClassInfo,
        transmissionInfo,
        annualDistanceDriven,
        annualEmissions,
        annualFuelConsumption,
        annualFuelCost,
    };
};

// Functions specific to EVs:

function getIndicesToUpdate(startTime, endTime) {
    let indicesToUpdate = [];
    if (startTime < endTime) { // determine the indices to update
        for (let i = startTime; i < endTime; i++) {
            indicesToUpdate.push(i);
        }
    } else { // if span over midnight
        for (let i = startTime; i < 24; i++) {
            indicesToUpdate.push(i);
        }
        for (let i = 0; i < endTime; i++) {
            indicesToUpdate.push(i);
        }
    }
    return indicesToUpdate;
}

function generateRateAndPeriod(dataFrame, TOU, electricityPrice, weekdayScheduleTOU, weekendScheduleTOU, periodPrices) {
    const hoursInYear = 8760;

    if (TOU) { // if TOU is active, calculate the rate and period for each hour in the year based on the TOU schedule
        for (let i = 0; i < hoursInYear; i++) {
            const timestamp = dataFrame[i].timestamp;
            const { period, rate } = getPeriod(weekdayScheduleTOU, weekendScheduleTOU, timestamp, periodPrices);
            dataFrame[i].rate = rate;
            dataFrame[i].period = period;
        }
    } else { // if TOU is not active, set the rate to the flat electricity price and period to "Flat" for each hour in the year
        for (let i = 0; i < hoursInYear; i++) {
            dataFrame[i].rate = electricityPrice;
            dataFrame[i].period = "Flat";
        }
    }    

    return dataFrame;
}

function hourToTimestamp(hour) {
    // provide a default year if none is set
    const defaultYear = new Date().getFullYear(); // use the current year as a default
    const validYear = defaultYear;

    let baseDate = new Date(validYear, 0, 1);
    baseDate.setHours(hour);
    return baseDate;
}

function initializeDecisionSchedule(chargingHomeHourStart, chargingHomeHourEnd, v2gHourStart, v2gHourEnd) {
    let decisionCharge = [Array.from({ length: 24 }, (_, index) => index), Array(24).fill(0), Array(24).fill(0)];

    let chargingIndices = getIndicesToUpdate(chargingHomeHourStart, chargingHomeHourEnd); // get indices to update for charging hours

    chargingIndices.forEach((index) => { // update charge row (row 1) with 1s at charging indices
        decisionCharge[1][index] = 1;
    });

    let dischargeIndices = getIndicesToUpdate(v2gHourStart, v2gHourEnd); // get indices to update for V2G hours

    dischargeIndices.forEach((index) => { // update discharge row (row 2) with 1s at V2G indices
        decisionCharge[2][index] = 1;
    });

    return decisionCharge;
}

function reserveFloorEnergy(ev, timestamp, commuteDistance) {
    if (!isCommuteDay(timestamp)) { // if its not a commute day
        return ev.minEnergy; // no commute today or tomorrow
    }

    const hour = timestamp.getHours(); // get the hour
    const energyPerKilometer = ev.capacity / ev.range; // calculate the energy per kilometer
    const oneWayEnergy = energyPerKilometer * (commuteDistance / 2); // calculate the energy needed to travel the distance one way
    const roundTripEnergy = energyPerKilometer * commuteDistance; // calculate the roundtrip energy needed to cover the full distance of a commute
    
    let commuteEnergy;
    if (hour < ev.workHourStart) { // before work
        commuteEnergy = roundTripEnergy; // we need at least roundtrip commute energy
    } else if (hour >= ev.workHourStart && hour < ev.workHourEnd) { // during work
        commuteEnergy = oneWayEnergy; // only need half of the roundtrip energy to get home
    } else if (timestamp.getDay() < 5) { // if its a weekday, and tomorrow is a weekday
        commuteEnergy = roundTripEnergy; // we need at least roundtrip commute energy
    } else { // if the next day is a work day
        commuteEnergy = 0;
    }

    const floor = ev.minEnergy + commuteEnergy; // floor calculated as the minimum energy allowed by the EV configuration + next roundtrip commute energy
    return Math.min(floor, ev.maxEnergy); // set the floor
}

function hoursRemainingInDischargeWindow(ev, timestamp) {
    let currentHour = timestamp.getHours(); // get the hour
    let dischargeRow = ev.decisionCharge[2]; // V2G discharge schedule (1 = scheduled)

    if (dischargeRow[currentHour] !== 1) return 0 // not currently in a scheduled V2G window

    const windowSlice = dischargeRow.slice(currentHour); // slice forward from current hour
    const firstChange = windowSlice.findIndex(x => x !== 1); // find the first unscheduled hour
    const remaining = firstChange === -1 ? windowSlice.length : firstChange; // calculate remaining hours in discharge window

    return Math.max(remaining, 1); // return at least 1 hour remaining in the window (avoid division by 0)
}

function hoursRemainingInCurrentPeriod(ev, timestamp, dataFrame, currentHour) {
    const totalHours = Object.keys(dataFrame).length; // total number of rows in dataFrame
    const currentPeriod = dataFrame[currentHour].hasOwnProperty('period') ? dataFrame[currentHour].period : null; // get the current period from the TOU column

    if (currentPeriod === null) return 1 // if there is no period column

    // slice from currentHour and onward and find where the period changes
    let firstChange = totalHours; // default to end of df if no change found
    for (let i = currentHour; i < totalHours; i++) {
        if (dataFrame[i].period !== currentPeriod) {
            firstChange = i;
            break;
        }
    }
    let count = firstChange - currentHour;

    if (isCommuteDay(timestamp)) { // if it is a commute day (weekday)
        const hour = timestamp.getHours(); // get the hour
        if (hour < ev.workHourStart) { // if its before work
            const hoursUntilCommute = ev.workHourStart - hour; // count the number of hours until commute
            count = Math.min(count, hoursUntilCommute); // adjust the number of hours so that it accounts for when we have to go to work
        }
    }

    return Math.max(count, 1);

}

function hoursUntilNextDeadline(ev, timestamp, V2G) {
    const currentHour = timestamp.getHours(); // get the current hour of the day (0-23)
    const currentWeekday = (timestamp.getDay() + 6) % 7; // monday (0) - sunday (6)

    const hoursToNextWeekdayAt = (targetHour) => { // calculates the number of hours to the next weekday at the given target hour.
        let daysUntilWeekday;

        if (currentWeekday < 5) { // currently a weekday
            daysUntilWeekday = currentWeekday < 4 ? 1 : 3; // monday to thursday is tomorrow, friday = monday
        } else { // saturday or sunday
            daysUntilWeekday = 7 - currentWeekday; // saturday = 2 days away, sunday = 1
        }

        return (24 - currentHour) + (daysUntilWeekday - 1) * 24 + targetHour;
    };

    if (V2G) { // V2G enabled
        if (isCommuteDay(timestamp)) { // if its a weekday

            if (currentHour < ev.v2gHourStart) { // before V2G window
                return ev.v2gHourStart - currentHour;

            } else if (currentHour < ev.workHourStart) { // inside V2G window
                return ev.workHourStart - currentHour;
                
            } else { // at work or after work
                return hoursToNextWeekdayAt(ev.v2gHourStart);
            }
        } else { // stretch hours to Monday
            return hoursToNextWeekdayAt(ev.v2gHourStart);
        }
    } else { // no V2G
        if (isCommuteDay(timestamp)) { // if its a weekday
            if (currentHour < ev.workHourStart) { // before work
                return ev.workHourStart - currentHour;

            } else if (currentHour >= ev.workHourEnd) { // after work
                return hoursToNextWeekdayAt(ev.workHourStart);

            } else { // at work
                return Math.max(ev.workHourStart - currentHour, 1);
            }
        } else { // weekend
            return hoursToNextWeekdayAt(ev.workHourStart); // stretch hours to Monday
        }
    }
}

function loadAwareViableHoursAndRate(ev, dataFrame, timestamp, currentHour, loadThreshold, commuteDistance, charging) {
    // Step 1: slice forecast load from current hour to end of day
    const forecastSlice = Object.values(dataFrame).slice(currentHour).map(row => row.houseLoad_kWh);
    const n = forecastSlice.length;

    const scheduledHomeHours = ev.decisionCharge[1]
        .map((val, idx) => (val === 1 ? idx : -1))
        .filter(idx => idx !== -1);

    const hoursUntilDeadline = hoursUntilNextDeadline(ev, timestamp, false);

    const viableMask = [];
    for (let i = 0; i < n; i++) {
        const hourOfDay = (timestamp.getHours() + i) % 24; // which hour each offset falls on
        const offsetDays = Math.floor((timestamp.getHours() + i) / 24); // which day each offset falls on
        const absoluteWeekday = (timestamp.getDay() + offsetDays) % 7; // actual weekday
        const isWeekendHour = absoluteWeekday === 0 || absoluteWeekday === 6; // true for saturdays and sundays

        const load = forecastSlice[i];
        // Step 2.1: build the threshold mask based on charge/discharge direction
        const thresholdOk = charging ? load < loadThreshold : load > loadThreshold;

        // Step 2.2: work hours mask restricted to work hours on actual weekdays
        const isWorkHour = hourOfDay >= ev.workHoursStart && hourOfDay < ev.workHoursEnd;
        const workOk = isWeekendHour || !isWorkHour; // weekend hours always viable / weekday work hours not viable

        // Step 2.3: home mask
        let homeOk = true;
        if (charging) {
            homeOk = isWeekendHour || scheduledHomeHours.includes(hourOfDay);
        }

        // Step 2.4: deadline mask (friday evening arrival to monday morning departure)
        const withinDeadline = i < hoursUntilDeadline;
        viableMask.push(thresholdOk && workOk && homeOk && withinDeadline);
    }

    // Step 3: find the first non-viable hour, and end the window there
    let viableHours;
    if (viableMask.every(v => v)) {
        viableHours = viableMask.length;
    } else if (!viableMask[0]) {
        viableHours = 0;
    } else {
        viableHours = viableMask.findIndex(v => !v); // index of first false
    }

    if (viableHours <= 0) {
        return [1, 0.0]; // avoid division by zero, this handles zero-rate case
    }

    // Step 4: calculate the even rate across viable hours
    let evenRate;
    if (charging) {
        const energyNeeded = ev.maxEnergy - ev.currentEnergy;
        evenRate = Math.min(energyNeeded / viableHours, ev.inverterMaxCharge);
    } else {
        const gap = Math.max(0, ev.currentEnergy - reserveFloorEnergy(ev, timestamp, commuteDistance));
        evenRate = Math.min(gap / viableHours, ev.inverterMaxDischarge);
    }

    return [viableHours, evenRate];
}

function determineEvDecision(ev, timestamp, rate, houseLoad, loadThreshold, V2G, commuteDistance) {
    const required = reserveFloorEnergy(ev, timestamp, commuteDistance); // calculates the amount of energy needed for a roundtrip commute
    const hour = timestamp.getHours(); // timestamp is already a datetime here
    const isWeekend = !isCommuteDay(timestamp);
    const isHome = hour >= ev.chargingHomeHourStart || hour < ev.chargingHomeHourEnd || isWeekend;
    let decision;

    // default case
    if (ev.chargeProgram.toLowerCase() === "none") {
        decision = isHome ? "charge" : "idle"; // if the EV is home, charge; if not, idle
    }

    // price-based case (requires V2G and TOU enabled)
    if (ev.chargeProgram.toLowerCase() === "arbitrage") {
        if (isHome) {
            // if the EV is home
            if (rate <= ev.arbitrageBuyPrice) {
                // if the energy buy conditions met (e.g., on-peak hours)
                decision = "charge"; // charge because we're at a price we're comfortable paying per kWh
            } else if (rate >= ev.arbitrageSellPrice) {
                // if the energy sell conditions met (e.g., off-peak hours)
                decision = "discharge"; // discharge because we're at a price where we could make some money giving energy back to the grid
            } else {
                // no conditions were met
                decision = "idle"; // idle, do nothing
            }
        } else {
            // if the EV is NOT home
            decision = "idle"; // idle, do nothing
        }
    }

    // load-based case
    if (ev.chargeProgram.toLowerCase() === "load-based") {
        if (isHome) {
            // if EV is home
            if (ev.currentEnergy < required) {
                // force recovery charge
                decision = "charge";
            } else if (houseLoad < loadThreshold) {
                // if houseLoad is below the threshold
                decision = "charge"; // charge (up to the threshold)
            } else if (houseLoad > loadThreshold) {
                // if houseLoad is above the threshold
                if (V2G && ev.currentEnergy > required) {
                    decision = "discharge"; // discharge (down to the threshold)
                } else {
                    decision = "idle"; // idle, do nothing
                }
            } else {
                // no conditions were met, or the load is perfectly at the threshold and no action should be taken
                decision = "idle"; // idle, do nothing
            }
        } else {
            decision = "idle"; // idle, do nothing
        }
    }

    return decision; // return a verdict
}

function determineEnergyTransfer(ev, commuteDistance, deltaT, decision, dataFrame, timestamp, V2G, TOU) {
    let proposedCurrentEnergy = 0;
    let powerTransferValue = 0;
    const startEnergy = ev.currentEnergy;
    let hoursUntilChange;

    // // load-leveling logic
    // if (TOU) {
    //     // if a set schedule must be considered
    //     hoursUntilChange = detectChangeInPeriod(dataFrame, timestamp);
    // } else {
    //     // follow home schedule
    //     if (timestamp.getHours() >= ev.chargingHomeHourStart) {
    //         // after work
    //         hoursUntilChange = 24 - timestamp.getHours() + ev.chargingHomeHourEnd;
    //     } else if (timestamp.getHours() < ev.chargingHomeHourEnd) {
    //         // before work
    //         hoursUntilChange = ev.chargingHomeHourEnd - timestamp.getHours();
    //     } else {
    //         // at work or commuting
    //         hoursUntilChange = 0;
    //     }
    // }

    // calculate hours until change based on the current time and the charging home hours
    if (timestamp.getHours() >= ev.chargingHomeHourStart) {
        // if home (after work)
        hoursUntilChange = 24 - timestamp.getHours() + ev.chargingHomeHourEnd;
    } else if (timestamp.getHours() < ev.chargingHomeHourEnd) {
        // if home (before work)
        hoursUntilChange = ev.chargingHomeHourEnd - timestamp.getHours();
    } else {
        // if not home (at work or commuting)
        hoursUntilChange = 0;
    }

    let currEnergy = 0;
    let currSoc = 0;

    if (ev.chargePreference.toLowerCase() === "uncontrolled") {
        // charging/discharging as fast as max inverter rates allow

        if (decision === "charge") {
            proposedCurrentEnergy = ev.inverterMaxCharge * deltaT + ev.currentEnergy; // proposedCurrentEnergy = max. inverter charge rate * time; in kWh
            currEnergy = Math.min(ev.maxEnergy, proposedCurrentEnergy); // currEnergy = min(maxEnergy, proposedCurrentEnergy) **forces the current energy to be no higher than the max. capacity of the battery (in kWh)
            powerTransferValue = currEnergy - startEnergy; // powerTransferValue = currEnergy - startEnergy; in kWh

            //console.log(`${timestamp.getHours()} - charge: ${powerTransferValue}, currSoc: ${currSoc = (currEnergy / ev.capacity) * 100}`); // logging for debugging
        } else if (decision === "discharge") {
            proposedCurrentEnergy = -1 * (ev.inverterMaxDischarge * deltaT) + ev.currentEnergy; // proposedCurrentEnergy = -1 * (max. inverter discharge rate * time); in kWh
            currEnergy = Math.max(ev.minEnergy, proposedCurrentEnergy); // currEnergy = max(minEnergy, proposedCurrentEnergy) **forces the current energy to be no lower than the min. capacity of the battery (in kWh)
            powerTransferValue = (startEnergy - currEnergy) * -1; // adding sign change to powerTransferValue to indicate that the energy is being discharged (in kWh)
            //console.log(`${timestamp.getHours()} - discharge: ${powerTransferValue}, currSoc: ${currSoc = (currEnergy / ev.capacity) * 100}`); // logging for debugging
        } else if (decision === "idle") {
            powerTransferValue = 0; // no power transfer while idle
            currEnergy = ev.currentEnergy; // no change to current energy state
            //console.log(`${timestamp.getHours()} - idle: ${powerTransferValue}, currSoc: ${currSoc = (currEnergy / ev.capacity) * 100}`); // logging for debugging
        } else if (decision === "commuteUpdate") {
            let drivingEnergyDischargeRate = ev.capacity / ev.range; // drivingEnergyDischargeRate = (capacity / range); in kWh - this value is derived from original capacity not the ev.maxEnergy rate
            let energyWithdrawn = drivingEnergyDischargeRate * commuteDistance; // energyWithdrawn = drivingEnergyDischargeRate * commuteDistance; in kWh
            proposedCurrentEnergy = ev.currentEnergy - energyWithdrawn; // proposedCurrentEnergy = current energy - energyWithdrawn; in kWh
            currEnergy = Math.max(0, proposedCurrentEnergy); // currEnergy = max(0, proposedCurrentEnergy); **forces the current energy to be no lower than min. allowed energy amount (in kWh)
            powerTransferValue = startEnergy - currEnergy; // powerTransferValue = startEnergy - currEnergy; in kWh
            //console.log(`${timestamp.getHours()} - commuteUpdate: ${powerTransferValue}, currSoc: ${currSoc = (currEnergy / ev.capacity) * 100}`); // logging for debugging
        }

        currSoc = (currEnergy / ev.capacity) * 100; // update current state of charge (in %)
    }

    if (ev.chargePreference.toLowerCase() === "load levelling") {
        // charging/discharging based on the time spent at home not commuting

        if (decision === "charge") {
            let targetRate = Math.abs(ev.maxEnergy - ev.currentEnergy) / hoursUntilChange; // target rate = (max. ev capacity - current energy state) / hours until work
            let chargeEnergy = Math.min(targetRate, ev.inverterMaxCharge); // chargeEnergy = min(targetRate, inverter max charge) **forces the inverter to charge no higher than the max rate
            proposedCurrentEnergy = ev.currentEnergy + chargeEnergy; // proposedCurrentEnergy = current energy + chargeEnergy
            currEnergy = Math.min(ev.maxEnergy, proposedCurrentEnergy); // currEnergy = min(maxEnergy, proposedCurrentEnergy) **forces the current energy to be no higher than the max. capacity of the battery
            powerTransferValue = currEnergy - startEnergy;

            //console.log(`charge`);
            // console.log(
            //     `${timestamp.getHours()} - charge: ${powerTransferValue}, currSoc: ${(currSoc =
            //         (currEnergy / ev.capacity) * 100)}`
            // ); // logging for debugging
        } else if (decision === "discharge") {
            let targetRate = Math.abs(ev.minEnergy - ev.currentEnergy) / hoursUntilChange; // spread electricity discharge amount over remaining hours until work
            let dischargeEnergy = Math.min(targetRate, ev.inverterMaxDischarge); // check that discharge rate doesn't exceed inverter max discharge rate capabilities
            proposedCurrentEnergy = ev.currentEnergy - dischargeEnergy; // calculate/estimate discharge for the current hour
            currEnergy = Math.max(ev.minEnergy, proposedCurrentEnergy); // check that proposed energy change doesn't fall below the minimum energy state
            powerTransferValue = startEnergy - currEnergy; // adjust energy state

            //console.log(`discharge: ${startEnergy} - ${currEnergy}`);
            // console.log(
            //     `${timestamp.getHours()} - discharge: ${powerTransferValue}, currSoc: ${(currSoc =
            //         (currEnergy / ev.capacity) * 100)}`
            // );
        } else if (decision === "idle") {
            powerTransferValue = 0; // no power transfer while idle
            currEnergy = ev.currentEnergy; // no change to current energy state

            //console.log(`idle`);
            // console.log(
            //     `${timestamp.getHours()} - idle: ${powerTransferValue}, currSoc: ${(currSoc =
            //         (currEnergy / ev.capacity) * 100)}`
            // ); // logging for debugging
        } else if (decision === "commuteUpdate") {
            let drivingEnergyDischargeRate = ev.capacity / ev.range; // drivingEnergyDischargeRate = (capacity / range); in kWh - this value is derived from original capacity not the ev.maxEnergy rate
            let energyWithdrawn = drivingEnergyDischargeRate * commuteDistance; // energyWithdrawn = drivingEnergyDischargeRate * commuteDistance; in kWh
            proposedCurrentEnergy = ev.currentEnergy - energyWithdrawn; // proposedCurrentEnergy = current energy - energyWithdrawn; in kWh
            currEnergy = Math.max(0, proposedCurrentEnergy); // currEnergy = max(0, proposedCurrentEnergy); **forces the current energy to be no lower than min. allowed energy amount (in kWh)
            powerTransferValue = startEnergy - currEnergy; // powerTransferValue = startEnergy - currEnergy; in kWh

            //console.log(`commute`);
            // console.log(
            //     `${timestamp.getHours()} - commuteUpdate: ${powerTransferValue}, currSoc: ${(currSoc =
            //         (currEnergy / ev.capacity) * 100)}`
            // ); // logging for debugging
        }

        currSoc = (currEnergy / ev.capacity) * 100; // update current state of charge (in %)
    }

    return { powerTransferValue, currSoc, currEnergy }; // send values back to runSimStep
}

function determineDecision(ev, timestamp, rate) {
    let decision = "";
    const datetime = timestamp;
    const hour = datetime.getHours();

    if (ev.chargeProgram.toLowerCase() === "none") {
        // ev charges when it gets home, nothing else
        if (hour >= ev.chargingHomeHourStart || hour < ev.chargingHomeHourEnd) {
            // if home
            decision = "charge";
        } else {
            // if not home
            decision = "idle";
        }
    }

    if (ev.chargeProgram.toLowerCase() === "arbitrage") {
        // ev charges (buys) and discharges (sells) electricity based on the price (TOU and V2G needed)
        // ev charges when off-peak and discharges when on-peak
        if (hour >= ev.chargingHomeHourStart || hour < ev.chargingHomeHourEnd) {
            // if home
            if (rate <= ev.arbitrageBuyPrice / 100) {
                // buy electricity
                decision = "charge";
            } else if (rate >= ev.arbitrageSellPrice / 100) {
                // sell electricity
                decision = "discharge";
            } else {
                // do nothing
                decision = "idle";
            }
        } else {
            // if not home
            decision = "idle"; // do nothing
        }
    }

    if (ev.chargeProgram === "demand-response") {
        // ev charges as soon as it gets home, and discharges only when called, implement demand-response logic if needed
        // do something here
    }

    return decision;
}

function determineEvEnergyTransfer(ev, deltaT, decision, dataFrame, currentHour, loadThreshold, commuteDistance) {
    let evPowerTransferValue = 0;
    let eLoss = 0;
    const startEnergy = ev.currentEnergy;

    const timestamp = dataFrame[currentHour].timestamp;
    const indexOfTimestampHour = timestamp.getHours();

    const eff = ev.roundTripEfficiency / 100;
    const reserveFloor = reserveFloorEnergy(ev, timestamp, commuteDistance);
    const isLoadAware = ev.chargeProgram.toLowerCase() === "load-based";
    const currentForecast = isLoadAware ? dataFrame[currentHour]["houseLoad_kWh"] : 0;

    if (decision === "discharge" && ev.currentEnergy <= reserveFloor) return [0, 0];

    // UNCONTROLLED
    if (ev.chargePreference.toLowerCase() === "uncontrolled") { // charging/discharging as fast as max inverter rates allow

        // UNCONTROLLED - IDLE (AND LOAD AWARE)
        if (isLoadAware && decision === "idle") { // force recovery charge
            const isHome = indexOfTimestampHour >= ev.chargingHomeHourStart || indexOfTimestampHour < ev.chargingHomeHourEnd || !isCommuteDay(timestamp); // check if home

            if (isHome && ev.currentEnergy < reserveFloor) { // if recovery charge is needed
                const [viableHours, evenRate] = loadAwareViableHoursAndRate( // get the viable hours, ignore the even rate produced (this is not even-rate)
                    ev, dataFrame, timestamp, currentHour, loadThreshold, commuteDistance, true
                );

                if (viableHours === 0) { // if no viable hours
                    decision = "charge"; // charge now, don't wait

                } else { // if viable hours found
                    const forecastWindow = Object.values(dataFrame).slice(currentHour, currentHour + viableHours - 1).map(row => row.houseLoad_kWh); // find the position of the viable hours
                    const minIndex = forecastWindow.indexOf(Math.min(...forecastWindow));
                    
                    if (minIndex === 0) { // if we're in the forecastWindow
                        decision = "charge"; // charge now
                    }
                }
            }
        }

        // UNCONTROLLED - CHARGE
        if (decision === "charge") { // get current proposed contribution by multiplying power of inverter by deltaT to get the energy contribution, add to current capacity
            let gridDraw = ev.inverterMaxCharge * deltaT; // maximum output at this time, grid-side (deltaT = 1)

            if (isLoadAware) { // for load-based case (charging max-rate, aka "max-rate")
                if (ev.currentEnergy < reserveFloor) { // recovery charge
                    const recoveryNeeded = reserveFloor - ev.currentEnergy; // calculate recovery charge needed
                    gridDraw = Math.min(recoveryNeeded / eff, ev.inverterMaxCharge); // calculate what will actually be drawn from the grid
                } else { // clamp to not exceed house load threshold
                    const headroom = Math.max(0, loadThreshold - currentForecast); // calculate headroom until load-based threshold ("how much can we charge at max rate without going over threshold?")
                    gridDraw = Math.min(gridDraw, headroom); // clamp grid draw to threshold if necessary
                }
            }

            const batteryGain = gridDraw * eff; // what the battery will actually see come in
            ev.currentEnergy = Math.min(ev.maxEnergy, ev.currentEnergy + batteryGain); // update current energy of the EV
            const batteryDelta = ev.currentEnergy - startEnergy; // change in battery charge
            const actualGridDraw = batteryDelta / eff; // calculate what the grid actually produced (based on efficiency)

            evPowerTransferValue = actualGridDraw; // set the power transfer value as the grid-side draw
            eLoss = actualGridDraw - batteryDelta; // calculate the efficiency loss
        
        // UNCONTROLLED - DISCHARGE
        } else if (decision === "discharge") { // get current proposed contribution by multiplying power of inverter by deltaT to get the energy contribution, add to current capacity
            let proposedDischarge = ev.inverterMaxDischarge * deltaT; // maximum output at this time (deltaT = 1)

            if (isLoadAware) { // for the load-based case
                const excess = Math.max(0, currentForecast - loadThreshold); // calculate excess (amount over threshold)
                proposedDischarge = Math.min(proposedDischarge, excess); // adjust discharge to clamp discharge to ONLY bring the load down the the threshold
            }

            //ev.currentEnergy = Math.max(ev.minEnergy, ev.currentEnergy - proposedDischarge); // update current energy in EV
            ev.currentEnergy = Math.max(reserveFloor, ev.currentEnergy - proposedDischarge);
            evPowerTransferValue = ev.currentEnergy - startEnergy; // update power transfer value
            eLoss = 0; // no efficiency loss
        
        // UNCONTROLLED - IDLE
        } else if (decision === "idle") {
            evPowerTransferValue = 0;
            eLoss = 0;
        
        // UNCONTROLLED - COMMUTE
        } else if (decision === "commuteUpdate") { // get energy discharge from range, so divide the total capacity of the EV by the range, for a value of kWh/km
            const energyWithdrawn = (ev.capacity / ev.range) * (commuteDistance / 2); // calculate energy withdrawn for commute based on commute distance
            ev.currentEnergy = Math.max(ev.minEnergy, ev.currentEnergy - energyWithdrawn); // update current energy in EV
            evPowerTransferValue = ev.currentEnergy - startEnergy; // update power transfer value
            eLoss = 0; // no efficiency loss
        }

        ev.currentSoc = (ev.currentEnergy / ev.capacity) * 100; // update SOC value with percentage
        return [evPowerTransferValue, eLoss];

    }

    if (ev.chargePreference.toLowerCase() === "load leveling") {

        // LOAD LEVELING - CHARGE
        if (decision === "charge") {
            if (ev.currentEnergy >= ev.maxEnergy) { // limit charge if battery is already at max allowed charge
                return [0, 0];
            }

            let chargeRate = 0;
            if (isLoadAware) { // if load-based charging:
                const [viableHours, evenRate] = loadAwareViableHoursAndRate( // calculate the viable hours (uses "look-ahead" logic) and even rate based on these hours
                    ev, dataFrame, timestamp, currentHour, loadThreshold, commuteDistance, true
                );
                
                if (ev.currentEnergy < reserveFloor) { // recovery charge (ignores headroom)
                    const recoveryNeeded = reserveFloor - ev.currentEnergy; // calculate recovery charge: reserve floor = roundtrip commute + min SOC; currentEnergy = energy currently inside the EV battery
                    const [recoveryViableHours, _ ] = loadAwareViableHoursAndRate(
                        ev, dataFrame, timestamp, currentHour, Infinity, commuteDistance, true
                    ); // pref #1 for recovery charge: spread across hours below threshold at home
                    
                    if (recoveryViableHours > 0) { // pref #1 continued if conditions met
                        chargeRate = Math.min(recoveryNeeded / Math.max(recoveryViableHours, 1), ev.inverterMaxCharge); // chargeRate = either the amount of recovery charge needed over lowest hours threshold hours OR limit to the maximum inverter charge rate if the recovery charge is greater

                    } else { // pref #2 for recovery charge: if no hour sbelow threshold during home window... spread recovery across all home hours
                        const [homeViableHours, _ ] = loadAwareViableHoursAndRate(
                            ev, dataFrame, timestamp, currentHour, Infinity, commuteDistance, true
                        );
                        chargeRate = Math.min(recoveryNeeded / Math.max(homeViableHours, 1), ev.inverterMaxCharge);
                    }
                    
                } else { // clamp to stay below load threshold
                    const headroom = Math.max(0, loadThreshold - currentForecast); // calculate headroom
                    chargeRate = Math.min(evenRate, headroom * eff); // chargeRate = either the smoothed rate over viable home hours (below threshold) OR charge rate needed to clamp to threshold
                }

            } else { // non-load-based
                const energyNeeded = ev.maxEnergy - ev.currentEnergy; // calculate energy needed

                if (energyNeeded <= 0) return [0, 0]; // if no energy needed

                const [_, evenRate] = loadAwareViableHoursAndRate( // np.inf: thresholdMask all "true," meaning only schedule masks will apply (e.g., home, work, V2G)
                    ev, dataFrame, timestamp, currentHour, Infinity, commuteDistance, true
                ); 
                chargeRate = evenRate;
            }

            ev.currentEnergy = Math.min(ev.maxEnergy, ev.currentEnergy + chargeRate); // update current energy of the EV
            const batteryDelta = ev.currentEnergy - startEnergy; // change in battery charge
            const actualGridDraw = batteryDelta / eff; // calculate what the grid actually produced (based on efficiency)

            evPowerTransferValue = actualGridDraw; // set the power transfer value as the grid-side draw
            eLoss = actualGridDraw - batteryDelta; // calculate the efficiency loss
            
        // LOAD LEVELING - DISCHARGE
        } else if (decision === "discharge") {

            let dischargeEnergy;
            if (isLoadAware) { // if load-based discharging:
                const [viableHours, evenRate] = loadAwareViableHoursAndRate( // calculate the viable hours (uses "look-ahead" logic) and even rate based on these hours
                    ev, dataFrame, timestamp, currentHour, loadThreshold, commuteDistance, false
                );
                const excess = Math.max(0, currentForecast - loadThreshold); // calculate the excess energy (above the threshold)
                dischargeEnergy = Math.min(evenRate, excess); // clamp discharge to only reduce load down to the threshold
            
            } else { // non load-based
                
                let windowHours;
                if (ev.decisionCharge[2][indexOfTimestampHour] === 1) { // if currently in V2G window
                    windowHours = Math.max(hoursRemainingInDischargeWindow(ev, timestamp), 1); // get the hours left in that window (non-TOU; no price signals - just the V2G schedule)
                
                } else { // if not in V2G window
                    windowHours = hoursRemainingInCurrentPeriod(ev, timestamp, dataFrame, currentHour); // find the hours left in current TOU period
                }
                
                const gap = Math.max(0, ev.currentEnergy - reserveFloor); // energy available to discharge above the commute floor
                dischargeEnergy = Math.min(gap / windowHours, ev.inverterMaxDischarge); // calculate even rate across remaining window
            }

            ev.currentEnergy = Math.max(reserveFloor, ev.currentEnergy - dischargeEnergy); // update current EV energy
            evPowerTransferValue = ev.currentEnergy - startEnergy; // update power transfer value
            eLoss = 0; // no efficiency loss
        
        // LOAD LEVELING - IDLE
        } else if (decision === "idle") {
            evPowerTransferValue = 0;
            eLoss = 0;

        // LOAD LEVELING - COMMUTE
        } else if (decision === "commuteUpdate") {
            const energyWithdrawn = (ev.capacity / ev.range) * (commuteDistance / 2); // calculate energy withdrawn for commute based on commute distance
            ev.currentEnergy = Math.max(ev.minEnergy, ev.currentEnergy - energyWithdrawn); // update current energy in EV
            evPowerTransferValue = ev.currentEnergy - startEnergy; // update power transfer value
            eLoss = 0; // no efficiency loss
        }

        ev.currentSoc = (ev.currentEnergy / ev.capacity) * 100;
        return [evPowerTransferValue, eLoss];
    }

    return [0, 0]; // fallback
}

function getPeriodAndRate(schedule, month, hour, periodPrices) {
    // helper function to get the period and rate (i.e., off-peak - 21 cents)
    const period = schedule[month][hour];
    const rate = periodPrices[period - 1] !== undefined ? parseFloat(periodPrices[period - 1].toFixed(3)) / 100 : 0;
    return { period, rate };
}

function getPeriod(weekdayScheduleTOU, weekendScheduleTOU, timestamp, periodPrices) {
    // get the period and rate based on the TOU schedule and a given date (timestamp format)
    const datetime = timestamp;
    const month = datetime.getMonth();
    const defaultWeekday = datetime.getDay(); // get the default weekday (0 = Sunday, 1 = Monday, ..., 6 = Saturday)
    const weekday = (defaultWeekday + 6) % 7; // adjust to have Monday as 0 and Sunday as 6
    const hour = datetime.getHours();

    const schedule = [5, 6].includes(weekday) ? weekendScheduleTOU : weekdayScheduleTOU; // select schedule based on day of week (weekend or weekday)

    return getPeriodAndRate(schedule, month, hour, periodPrices);
}

function isCommuteDay(timestamp) {
    return timestamp.getDay() >= 1 && timestamp.getDay() <= 5; // Monday to Friday are commute days
}

function runSimStep(
    ev,
    commuteDistance,
    dataFrame,
    currentHour,
    timestamp,
    TOU,
    V2G,
    electricityPrice,
    dischargeRow = 2,
    chargeRow = 1
) {
    const hour = timestamp.getHours();
    let rate = 0;
    let period = "None";
    let decision;
    let evPower = 0;
    let eLoss = 0;

    if (TOU) { // if TOU is enabled...
        const result = getPeriod(ev.weekdayScheduleTOU, ev.weekendScheduleTOU, timestamp, ev.periodPrices); // configure the price model
        period = result.period; // get period and rate derived from provided datetime
        rate = result.rate;
    } else { // non-TOU flat pricing configuration
        rate = electricityPrice;
        period = "Flat";
    }

    // commute only on weekdays and during designated commute times (aka: workHoursStart and workHoursEnd)
    if (isCommuteDay(timestamp) && (hour === ev.workHourStart || hour === ev.workHourEnd)) {
        decision = "commuteUpdate";
        [evPower, eLoss] = determineEvEnergyTransfer(ev, 1, decision, dataFrame, currentHour, ev.loadThreshold, commuteDistance); // determine power transfer of commute action

    } else { // handle non-commute hours
        const isWeekend = !isCommuteDay(timestamp); // check if its the weekend

        if (TOU) { // if TOU is enabled (uses price signals to decide an action to take; e.g., Off-Peak vs. On-Peak)
            decision = determineEvDecision(ev, timestamp, rate, dataFrame[currentHour]["houseLoad_kWh"], ev.loadThreshold, V2G, commuteDistance);
            if (!V2G && decision === "discharge") { // if V2G is not enabled, but the decision is to discharge, override the decision
                decision = "idle";
            }

        } else { // if TOU is NOT enabled (does not use price signals to decide an action)
            if (ev.chargeProgram.toLowerCase() === "load-based") { // this handles the case where we want to discharge outside of V2G hours to reduce houseload (this is more aggressive)
                decision = determineEvDecision(ev, timestamp, rate, dataFrame[currentHour]["houseLoad_kWh"], ev.loadThreshold, V2G, commuteDistance);

            } else if (V2G && ev.decisionCharge[dischargeRow][hour] === 1) { // if V2G is enabled, look into the decision schedule to decide action to take
                decision = "discharge"; // if the schedule says to discharge, we discharge

            } else if (ev.decisionCharge[chargeRow][hour] === 1) { // regardless of if V2G is enabled or NOT, charge when the schedule says to
                decision = "charge"; // charge because the schedule says to

            } else if (isWeekend) { // if its the weekend, there is no schedule defined
                decision = "charge"; // default to charging

            } else { // idle if theres not a charging or discharging action
                decision = "idle";
            }
        }
        [evPower, eLoss] = determineEvEnergyTransfer(ev, 1, decision, dataFrame, currentHour, ev.loadThreshold, commuteDistance); // energy transfer based on adjusted OR original decision
    }

    // only charging energy would be positive (regardless of path), therefore grossEV catches all values above 0
    const grossEv = Math.max(evPower, 0);
    const evProfile = evPower;
    const evSoc = ev.currentSoc;
    const evEnergy = ev.currentEnergy;
    const energyProfile = evProfile;
    const netPrice = energyProfile * rate;

    return { evProfile, evSoc, evEnergy, energyProfile, netPrice, decision, grossEv, eLoss, rate, period };
}

function calculateAnnualElectricVehicleEmissions(annualFuelConsumption, region) {
    const emissionsFactor = getEmissionFactorNIR(region, "elec"); // get emission factor by g/unitOfFuel
    let annualEmissions = annualFuelConsumption * emissionsFactor * 0.001; // convert to kg
    return annualEmissions;
}

function transformNestedDictToNestedArray(nestedDict) {
    // convert nested dictionary to nested array
    const numRows = Object.keys(nestedDict).length;
    const array = [];

    for (let i = 0; i < numRows; i++) {
        const row = [];
        for (let j = 0; j < 24; j++) {
            row.push(nestedDict[i][j]);
        }
        array.push(row);
    }
    return array;
}

function calculateElectricityConsumptionAndCost(commuteDistance, vehicleSpecs, fuelPrices, houseLoadProfile) {
    // calculate annual electricity consumption and cost
    const { electricityPrice } = fuelPrices; // get electricity prices
    const timesteps = 8760;
    let totalEnergyProfile = 0;
    let totalCost = 0;
    let commuteEvents = 0;

    const ev = {
        vehicleYear: vehicleSpecs.vehicleYear, // in YYYY format
        manufacturer: vehicleSpecs.manufacturer,
        model: vehicleSpecs.model,
        vehicleClass: vehicleSpecs.vehicleClass, // in L (liters) or cu. ft. (cubic feet)
        motor: vehicleSpecs.motor, // in kW
        transmission: vehicleSpecs.transmission,
        emissions: vehicleSpecs.emissions, // in g/km
        emissionRating: vehicleSpecs.emissionRating, // in g/km
        smogRating: vehicleSpecs.smogRating,
        fuelType: vehicleSpecs.fuelType,
        combinedFuelEconomy: vehicleSpecs.combinedFuelEconomy, // in L/100 km
        combinedFuelEconomyGasEquivalent: vehicleSpecs.combinedFuelEconomyGasEquivalent, // in L/100 equivalent
        rechargeTime: vehicleSpecs.rechargeTime, // in hours
        range: vehicleSpecs.range, // in km
        maxSoc: vehicleSpecs.maxSoc, // in %
        minSoc: vehicleSpecs.minSoc, // in %
        roundTripEfficiency: vehicleSpecs.roundTripEfficiency, // in %
        inverterMaxCharge: vehicleSpecs.chargerCapacity, // inverter max charge rate (in kW) = charger capacity * round trip efficiency
        inverterMaxDischarge: vehicleSpecs.chargerCapacity, // inverter max discharge rate (in kW) = charger capacity
        startSoc: vehicleSpecs.startSoc, // in %
        chargingHomeHourStart: parseInt(vehicleSpecs.chargingHomeHourStart), // in hours
        chargingHomeHourEnd: parseInt(vehicleSpecs.chargingHomeHourEnd), // in hours
        workHourStart: parseInt(vehicleSpecs.workHourStart), // in hours
        workHourEnd: parseInt(vehicleSpecs.workHourEnd), // in hours
        v2gHourStart: parseInt(vehicleSpecs.v2gHourStart), // in hours
        v2gHourEnd: parseInt(vehicleSpecs.v2gHourEnd), // in hours
        chargeProgram: vehicleSpecs.chargeProgram.toLowerCase(), // charge program (i.e., none, demand response, arbitrage)
        chargePreference: vehicleSpecs.chargePreference.toLowerCase(), // charge preference (i.e., load levelling, uncontrolled)
        loadThreshold: vehicleSpecs.loadThreshold, // load threshold (in kW)
        V2G: vehicleSpecs.enableV2G, // V2G enabled (true/false)
        TOU: vehicleSpecs.enableTOU, // TOU enabled (true/false)
        capacity: (vehicleSpecs.range * vehicleSpecs.combinedFuelEconomy) / 100, // in kWh
        currentSoc: vehicleSpecs.startSoc, // initialized to startSoc
        minEnergy: (vehicleSpecs.minSoc / 100) * ((vehicleSpecs.range * vehicleSpecs.combinedFuelEconomy) / 100), // minEnergy = (minSoc / 100) * (range * combinedFuelEconomy / 100); in kWh
        maxEnergy: (vehicleSpecs.maxSoc / 100) * ((vehicleSpecs.range * vehicleSpecs.combinedFuelEconomy) / 100), // maxEnergy = (maxSoc / 100) * (range * combinedFuelEconomy / 100); in kWh
        currentEnergy: (vehicleSpecs.startSoc / 100) * ((vehicleSpecs.range * vehicleSpecs.combinedFuelEconomy) / 100), // currentEnergy = (startSoc / 100) * (range * combinedFuelEconomy / 100); in kWh
        decisionCharge: initializeDecisionSchedule(
            parseInt(vehicleSpecs.chargingHomeHourStart),
            parseInt(vehicleSpecs.chargingHomeHourEnd),
            parseInt(vehicleSpecs.v2gHourStart),
            parseInt(vehicleSpecs.v2gHourEnd)
        ),
        periodPrices: vehicleSpecs.periodPrices, // in $/kWh
        weekdayScheduleTOU: transformNestedDictToNestedArray(vehicleSpecs.weekdayScheduleTOU), // convert nested dictionary to nested array
        weekendScheduleTOU: transformNestedDictToNestedArray(vehicleSpecs.weekendScheduleTOU), // convert nested dictionary to nested array
        arbitrageBuyPrice: vehicleSpecs.arbitrageBuyPrice / 100, // in $/kWh (arbitrageBuyPrice is entered in cents/kWh, same units as periodPrices)
        arbitrageSellPrice: vehicleSpecs.arbitrageSellPrice / 100, // in $/kWh (arbitrageSellPrice is entered in cents/kWh, same units as periodPrices)
    };

    let dataFrame = {};
    for (let index = 0; index < timesteps; index++) {
        dataFrame[index] = {
            evProfile: 0, 
            evSoc: 0, 
            evEnergy: 0, 
            netPrice: 0, 
            grossEv: 0,
            decision: "", 
            evCost: 0, 
            homeCost: 0, 
            evHomeExchange: 0,
            eCharge: 0, 
            eDischarge: 0, 
            wDrive: 0, 
            eBattIn: 0, 
            eLoss: 0,
            netHouseLoad: 0, 
            netElectricityPrice: 0, 
            homeElectricityPrice: 0,
            rate: 0, period: "None",
            timestamp: hourToTimestamp(index),
            houseLoad_kWh: houseLoadProfile[index],
        };
    }

    dataFrame = generateRateAndPeriod(dataFrame, ev.TOU, electricityPrice, ev.weekdayScheduleTOU, ev.weekendScheduleTOU, ev.periodPrices);

    for (let currentHour = 0; currentHour < timesteps; currentHour++) {
        const { evProfile, evSoc, evEnergy, netPrice, decision, grossEv, eLoss, rate, period } = runSimStep(
            ev,
            commuteDistance,
            dataFrame,
            currentHour,
            dataFrame[currentHour].timestamp,
            ev.TOU,
            ev.V2G,
            electricityPrice
        );

        dataFrame[currentHour]["evProfile"] = evProfile;
        dataFrame[currentHour]["evSoc"] = evSoc;
        dataFrame[currentHour]["evEnergy"] = evEnergy;
        dataFrame[currentHour]["grossEv"] = grossEv;
        dataFrame[currentHour]["decision"] = decision;
        dataFrame[currentHour]["eLoss"] = eLoss;
        dataFrame[currentHour]["rate"] = rate;
        dataFrame[currentHour]["period"] = period;

        if (decision === "charge") {
            dataFrame[currentHour]["eCharge"] = evProfile; // grid-side draw
            dataFrame[currentHour]["eBattIn"] = dataFrame[currentHour]["eCharge"] - dataFrame[currentHour]["eLoss"];
            dataFrame[currentHour]["evHomeExchange"] = evProfile;

        } else if (decision === "discharge") {
            dataFrame[currentHour]["eDischarge"] = evProfile;
            dataFrame[currentHour]["evHomeExchange"] = evProfile;
        
        } else if (decision === "commuteUpdate") {
            commuteEvents += 1;
            dataFrame[currentHour]["wDrive"] = evProfile;
            dataFrame[currentHour]["evHomeExchange"] = 0;
        
        } else {
            dataFrame[currentHour]["evHomeExchange"] = 0;
        }

        dataFrame[currentHour]["netHouseLoad"] = houseLoadProfile[currentHour] + dataFrame[currentHour]["evHomeExchange"];
        dataFrame[currentHour]["netElectricityPrice"] = dataFrame[currentHour]["netHouseLoad"] * dataFrame[currentHour]["rate"];
        dataFrame[currentHour]["homeElectricityPrice"] = houseLoadProfile[currentHour] * dataFrame[currentHour]["rate"];
        dataFrame[currentHour]["evCost"] = dataFrame[currentHour]["evHomeExchange"] * dataFrame[currentHour]["rate"];

        totalEnergyProfile += dataFrame[currentHour]["eCharge"];
        totalCost += dataFrame[currentHour]["evCost"];
    }

    const evProfile = Object.values(dataFrame).map(row => row["evProfile"]);

    /**console.table(dataFrame, [
        "timestamp", 
        "period", 
        "rate", 
        "decision", 
        "evProfile", 
        "evSoc", 
        "evEnergy",
        "eCharge", 
        "eDischarge", 
        "wDrive", 
        "eBattIn", 
        //"eLoss", 
        //"evHomeExchange", 
        "netHouseLoad", 
        "evCost"
    ]);*/

    return [totalEnergyProfile, totalCost, evProfile, commuteEvents];
}

export const getChargePreferenceOptions = (chargePreferenceMap) => {
    // get charge preference options from the map
    const chargePreferenceOpts = Object.keys(chargePreferenceMap).map((key) => ({
        value: key,
        label: chargePreferenceMap[key],
    }));
    return chargePreferenceOpts;
};

export const getChargeProgramOptions = (chargeProgramMap) => {
    // get charge program options from the map
    const chargeProgramOpts = Object.keys(chargeProgramMap).map((key) => ({
        value: key,
        label: chargeProgramMap[key],
    }));
    return chargeProgramOpts;
};

export const getPresetTOUOptions = (presetTOUMap) => {
    // get preset TOU options from the map
    const presetOptsTOU = Object.keys(presetTOUMap).map((key) => ({
        value: key,
        label: key,
    }));
    return presetOptsTOU;
};

// Main EV function:
function calculateAnnualDistanceDrivenWithCommuteEvents(commuteDistance, commuteEvents) {
    const annualDistanceDriven = (commuteDistance * (commuteEvents/2)); // commuteDistance x (commuteEvents / 2)
    return annualDistanceDriven; // in km
}

export const getElectricVehicleSpecs = (commuteDistance, vehicleSpecs, fuelPrices, hourlyLoadProfile) => {
    const fuelTypeInfo = getFuelType(vehicleSpecs.fuelType);
    const vehicleClassInfo = getVehicleClassInfo(vehicleSpecs.vehicleClass);
    const transmissionInfo = getTransmissionInfo(vehicleSpecs.transmission);

    let annualDistanceDriven = 0;
    let annualFuelConsumption = 0;
    let annualFuelCost = 0;
    let commuteEvents = 0;
    let evProfile = [];

    if (vehicleSpecs.vehicleYear && vehicleSpecs.manufacturer && vehicleSpecs.model && commuteDistance) {
        [annualFuelConsumption, annualFuelCost, evProfile, commuteEvents] = calculateElectricityConsumptionAndCost(
            commuteDistance,
            vehicleSpecs,
            fuelPrices,
            hourlyLoadProfile
        );
        annualDistanceDriven = calculateAnnualDistanceDrivenWithCommuteEvents(commuteDistance, commuteEvents);
    }

    const annualEmissions = calculateAnnualElectricVehicleEmissions(annualFuelConsumption, vehicleSpecs.region);

    return { fuelTypeInfo, vehicleClassInfo, transmissionInfo, annualDistanceDriven, annualEmissions, annualFuelConsumption, annualFuelCost, evProfile };
};

const calculateAnnualHybridFuelConsumptionAndCost = (commuteDistance, vehicleSpecs, fuelPrices) => {
    // calculate annual hybrid fuel consumption and cost
    const { regularGasPrice, premiumGasPrice, electricityPrice } = fuelPrices;

    const hybrid = {
        vehicleYear: vehicleSpecs.vehicleYear, // in YYYY format
        manufacturer: vehicleSpecs.manufacturer,
        model: vehicleSpecs.model,
        vehicleClass: vehicleSpecs.vehicleClass, // in L (liters) or cu. ft. (cubic feet)
        motor: vehicleSpecs.motor, // in kW
        engineSize: vehicleSpecs.engineSize, // in L
        cylinders: vehicleSpecs.cylinders, // in number of cylinders
        transmission: vehicleSpecs.transmission,
        emissions: vehicleSpecs.emissions, // in g/km
        emissionRating: vehicleSpecs.emissionRating, // in g/km
        smogRating: vehicleSpecs.smogRating,
        fuelType1: vehicleSpecs.fuelType1, // i.e., Diesel, Regular Gasoline, Premium Gasoline
        range1: vehicleSpecs.range1, // in km
        combinedFuelEconomy1: vehicleSpecs.combinedFuelEconomy1, // in L/100 km
        combinedElectricityEconomy1: vehicleSpecs.combinedElectricityEconomy1, // in kWh/100 km
        rechargeTime: vehicleSpecs.rechargeTime, // in hours
        fuelType2: vehicleSpecs.fuelType2, // Electricity
        range2: vehicleSpecs.range2, // in km
        combinedFuelEconomy2: vehicleSpecs.combinedFuelEconomy2, // in L/100 km equivalent
        region: vehicleSpecs.region, // region for emissions factor
    };

    // consumption calculations
    let annualFuelConsumption = 0;
    let annualElectricityConsumption = 0;
    let annualEmissions = 0;
    const electricityEmissionFactor = getEmissionFactorNIR(hybrid.region, "elec"); // get emission factor by g/unitOfFuel
    const fuelEmissionFactor = hybrid.emissions;

    for (let i = 0; i < 365; i++) {
        // go through everyday in the year
        if (commuteDistance <= hybrid.range1) {
            // electric-only mode
            let electricityUsed = (commuteDistance / 100) * hybrid.combinedElectricityEconomy1;
            let fuelConsumption = (commuteDistance / 100) * hybrid.combinedFuelEconomy1;
            annualElectricityConsumption += electricityUsed;
            annualFuelConsumption += fuelConsumption; //fuel used, if any, in electric only mode

            // electricity emissions
            let dailyEmissions = hybrid.range1 * electricityEmissionFactor;
            annualEmissions += dailyEmissions;
        } else {
            // gasoline mode
            let electricityUsed = (hybrid.range1 / 100) * hybrid.combinedElectricityEconomy1;
            let fuelConsumption = (hybrid.range1 / 100) * hybrid.combinedFuelEconomy1;
            annualElectricityConsumption += electricityUsed;
            annualFuelConsumption += fuelConsumption; // fuel used, if any, in electric only mode

            // electricity emissions
            let dailyEmissions = hybrid.range1 * electricityEmissionFactor;
            annualEmissions += dailyEmissions;

            let remainingDistance = commuteDistance - hybrid.range1; // find remaining distance
            let fuelConsumption_gasMode = (remainingDistance / 100) * hybrid.combinedFuelEconomy2; // get fuel use on gasoline only mode
            annualFuelConsumption += fuelConsumption_gasMode;

            // fuel emissions
            let dailyEmissions_gasMode = remainingDistance * fuelEmissionFactor;
            annualEmissions += dailyEmissions_gasMode;
        }
    }
    annualEmissions = annualEmissions / 1000; // g/CO2 to kg/CO2

    // cost calculations
    let annualFuelCost = 0;
    if (hybrid.fuelType1.includes("Electricity" || "B") && hybrid.fuelType1.includes("Regular Gasoline" || "X")) {
        // regular gas and electricity in electric only mode
        annualFuelCost = annualFuelConsumption * regularGasPrice + annualElectricityConsumption * electricityPrice;
    } else if (
        hybrid.fuelType1.includes("Electricity" || "B") &&
        hybrid.fuelType1.includes("Premium Gasoline" || "Z")
    ) {
        // premium gas and electricity in electric only mode
        annualFuelCost = annualFuelConsumption * premiumGasPrice + annualElectricityConsumption * electricityPrice;
    } else if (hybrid.fuelType1 === "Electricity" || "B") {
        // just "B" = electricity in electric only mode
        if (hybrid.fuelType2 === "Regular Gasoline" || "X") {
            // regular gas in gas mode
            annualFuelCost = annualFuelConsumption * regularGasPrice + annualElectricityConsumption * electricityPrice;
        }
        if (hybrid.fuelType2 === "Premium Gasoline" || "Z") {
            // premium gas in gas mode
            annualFuelCost = annualFuelConsumption * premiumGasPrice + annualElectricityConsumption * electricityPrice;
        }
    } else {
        annualFuelCost = 0;
        console.error("Incorrect Fuel type");
    }

    return [annualFuelConsumption, annualElectricityConsumption, annualFuelCost, annualEmissions]; // in L, in kWh, in $
};

export const getHybridVehicleSpecs = (commuteDistance, vehicleSpecs, fuelPrices) => {
    // get hybrid vehicle specs based on commute distance, vehicle specs, and fuel prices
    const fuelType1Info = getFuelType(vehicleSpecs.fuelType1);
    const fuelType2Info = getFuelType(vehicleSpecs.fuelType2);
    const vehicleClassInfo = getVehicleClassInfo(vehicleSpecs.vehicleClass);
    const transmissionInfo = getTransmissionInfo(vehicleSpecs.transmission);

    let annualFuelConsumption = null;
    let annualElectricityConsumption = null;
    let annualFuelCost = null;
    let annualEmissions = null;

    const annualDistanceDriven = calculateAnnualDistanceDriven(commuteDistance);

    if (vehicleSpecs.vehicleYear && vehicleSpecs.manufacturer && vehicleSpecs.model && commuteDistance) {
        [annualFuelConsumption, annualElectricityConsumption, annualFuelCost, annualEmissions] =
            calculateAnnualHybridFuelConsumptionAndCost(commuteDistance, vehicleSpecs, fuelPrices);
    }

    return {
        fuelType1Info,
        fuelType2Info,
        vehicleClassInfo,
        transmissionInfo,
        annualDistanceDriven,
        annualEmissions,
        annualFuelConsumption,
        annualElectricityConsumption,
        annualFuelCost,
    };
};
